import MessageStatusLabel from '@aurora/shared-client/components/messages/MessageStatusLabel/MessageStatusLabel';
import NodeIcon from '@aurora/shared-client/components/nodes/NodeIcon/NodeIcon';
import NodeTitle from '@aurora/shared-client/components/nodes/NodeTitle/NodeTitle';
import useSeoProperties from '@aurora/shared-client/components/seo/useSeoProperties';
import UserLogin from '@aurora/shared-client/components/users/UserLogin/UserLogin';
import UserRank from '@aurora/shared-client/components/users/UserRank/UserRank';
import type {
  MessagePageOrReplyPageAndParams,
  MessageParamPages,
  MessageReplyPageParams
} from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  Article,
  IdeaTopicMessage,
  OccasionTopicMessage
} from '@aurora/shared-generated/types/graphql-schema-types';
import { ConversationStyle } from '@aurora/shared-generated/types/graphql-schema-types';
import type { ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react';
import React, { forwardRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { FormattedDate } from 'react-intl';
import { getMessageRouteAndParams } from '../../../helpers/messages/MessageHelper/MessageHelper';
import { SubscriptionActionVariant } from '../../../types/enums';
import type {
  ContextMessageFragment,
  MessageRepliesCountFragment
} from '../../../types/graphql-types';
import type { ExtendedOverlayInjectedProps } from '../../extendedOverlayTrigger/ExtendedOverlayTrigger/ExtendedOverlayTrigger';
import KudosCount from '../../kudos/KudosCount/KudosCount';
import OccasionAttendeeCount from '../../occasions/OccasionAttendeeCount/OccasionAttendeeCount';
import OccasionLocationData from '../../occasions/OccasionLocationData/OccasionLocationData';
import OccasionTime from '../../occasions/OccasionTime/OccasionTime';
import OccasionType from '../../occasions/OccasionType/OccasionType';
import MessageBody from '../MessageBody/MessageBody';
import MessageRepliesCount from '../MessageRepliesCount/MessageRepliesCount';
import MessageSolvedBadge from '../MessageSolvedBadge/MessageSolvedBadge';
import MessageSubject from '../MessageSubject/MessageSubject';
import MessageSubscriptionAction from '../MessageSubscriptionAction/MessageSubscriptionAction';
import MessageTeaser from '../MessageTeaser/MessageTeaser';
import MessageViewCount from '../MessageViewCount/MessageViewCount';
import localStyles from './MessageHoverCard.module.pcss';

type MessageParams = MessageParamPages | MessageReplyPageParams;

interface Props extends ExtendedOverlayInjectedProps {
  /**
   * The message to create the card for.
   */
  entity: ContextMessageFragment;
}

/**
 * Displays Hover Card on hover of message link.
 *
 * @author Subhiksha Venkatesan
 */
const MessageHoverCard: ForwardRefExoticComponent<
  PropsWithoutRef<Props> & RefAttributes<HTMLElement>
> = forwardRef<HTMLDivElement, Props>(function UserHoverCardForward(
  { entity: message, style, onMouseEnter, onMouseLeave },
  ref
) {
  const cx = useClassNameMapper(localStyles);
  const { Link } = useEndUserRoutes();
  const { getCaseSensitivePath } = useSeoProperties();
  const { author, board, body, postTime, repliesCount, kudosSumWeight, metrics } = message;
  const { conversationStyle } = board;
  const { teaser } = message as Article;
  const occasion = message as OccasionTopicMessage;
  const { occasionData } = occasion;
  const Component = 'a';
  const showSolvedBadge = message.conversation.solved;
  const showMessageStatus = conversationStyle === ConversationStyle.Idea;
  const isOccasion = conversationStyle === ConversationStyle.Occasion;
  const showRepliesCount = repliesCount > 0;
  const showKudosCount = kudosSumWeight > 0;
  const showAttendeeCount = occasionData?.rsvps.totalCount > 0;
  const showViewsCount = metrics.views > 0 && !isOccasion;
  const showMetadata = showRepliesCount || showKudosCount || showViewsCount || showAttendeeCount;
  const hasAllMetaData = showRepliesCount && showKudosCount && showViewsCount && showAttendeeCount;

  /**
   * Renders the content avatar of the message.
   */
  function renderBoardIcon(): React.ReactElement {
    const { route, params } = getMessageRouteAndParams(message);
    const finalParams =
      params &&
      Object.fromEntries(Object.keys(params).map(key => [key, getCaseSensitivePath(params[key])]));
    return (
      <Link<MessagePageOrReplyPageAndParams>
        route={route}
        params={finalParams as unknown as MessageParams}
        passHref
        legacyBehavior={true}
      >
        <Component
          className={cx('lia-g-hovercard-icon lia-g-hovercard-link')}
          title={message.subject}
        >
          <NodeIcon node={board} className={cx('lia-icon')} />
        </Component>
      </Link>
    );
  }

  /**
   * Renders the subject of the message.
   */
  function renderSubject(): React.ReactElement {
    return (
      <MessageSubject
        message={message}
        className={cx('lia-g-mb-0 lia-g-clamp')}
        subjectLinkClassName={cx('lia-g-hovercard-title')}
        useTitle
      />
    );
  }

  /**
   * Renders the user login of the message.
   */
  function renderAuthorLogin(): React.ReactElement {
    return <UserLogin user={author} />;
  }

  /**
   * Renders post time of the message.
   */
  function renderMessagePostTime(): React.ReactElement {
    return <FormattedDate value={message.postTime} year="numeric" month="long" day="2-digit" />;
  }

  /**
   * Renders the user login and post time of the message.
   */
  function renderAuthorLoginAndMessagePostTime(): React.ReactElement {
    return (
      <div className={cx('lia-g-hovercard-meta-text lia-g-hovercard-divided-text')}>
        {renderAuthorLogin()}
        <div className={cx('lia-g-divider-element')} />
        {!isOccasion ? (
          postTime && renderMessagePostTime()
        ) : (
          <UserRank user={author} labelClassName={cx('lia-author-rank')} />
        )}
      </div>
    );
  }

  /**
   * Renders the parent node of the message.
   */
  function renderParentNode(): React.ReactElement {
    return <NodeTitle node={board} as="span" className={cx('lia-g-hovercard-meta-text')} />;
  }

  /**
   * Renders the solved badge of the message.
   */
  function renderSolvedBadge(): React.ReactElement {
    return <MessageSolvedBadge message={message} className={cx('lia-badge')} />;
  }

  /**
   * Renders the status of the idea message
   */
  function renderMessageStatus() {
    const messageStatus = (message as IdeaTopicMessage).status;
    return (
      messageStatus && (
        <MessageStatusLabel status={messageStatus} className={cx('lia-message-status')} />
      )
    );
  }

  /**
   * Renders the body of the message.
   */
  function renderMessageBody(): React.ReactElement {
    return (
      body.replaceAll(/\n|&nbsp;/g, '').trim() && (
        <MessageBody
          message={message}
          clampLines={3}
          className={cx('lia-g-hovercard-info')}
          useSearchSnippet={false}
        />
      )
    );
  }

  /**
   * Renders the teaser of the message.
   */
  function renderTeaser(): React.ReactElement {
    return (
      <MessageTeaser
        className={cx('lia-g-hovercard-info lia-info lia-g-clamp lia-g-clamp-3')}
        teaserContent={message as Article}
      />
    );
  }

  /**
   * If teaser is present render teaser otherwise render body.
   */
  function renderPreviewText(): React.ReactElement {
    return teaser && teaser.trim() ? renderTeaser() : renderMessageBody();
  }

  /**
   * Renders the views count of the message.
   */
  function renderViewsCount(): React.ReactElement {
    return (
      <MessageViewCount
        message={message}
        useTable
        countAs="h6"
        className={cx('lia-g-hovercard-data-item')}
        countClassName={cx('lia-g-hovercard-data-number')}
        textClassName={cx('lia-g-hovercard-data-title')}
      />
    );
  }

  /**
   * Renders the kudos count of the message.
   */
  function renderKudosCount(): React.ReactElement {
    return (
      <KudosCount
        message={message}
        useTable
        countAs="h6"
        className={cx('lia-g-hovercard-data-item')}
        countClassName={cx('lia-g-hovercard-data-number')}
        textClassName={cx('lia-g-hovercard-data-title')}
      />
    );
  }

  /**
   * Renders the replies count of the message.
   */
  function renderRepliesCount(): React.ReactElement {
    return (
      <MessageRepliesCount
        message={message as MessageRepliesCountFragment}
        conversationStyle={conversationStyle}
        useTable
        countAs="h6"
        className={cx('lia-g-hovercard-data-item')}
        countClassName={cx('lia-g-hovercard-data-number')}
        textClassName={cx('lia-g-hovercard-data-title')}
      />
    );
  }

  /**
   * Renders the attendee count for occasion.
   */
  function renderAttendeeCount() {
    return (
      <OccasionAttendeeCount
        message={occasion}
        countAs="h6"
        useTable
        className={cx('lia-g-hovercard-data-item')}
        countClassName={cx('lia-g-hovercard-data-number')}
        textClassName={cx('lia-g-hovercard-data-title')}
      />
    );
  }

  /**
   * Renders the occasion metadata.
   */
  function renderOccasionMetaData(): React.ReactElement {
    return (
      <>
        <OccasionType
          occasionType={occasionData?.occasionType}
          className={cx('lia-message-status')}
        />
        <OccasionTime occasion={occasionData} className={cx('lia-occasion lia-occasion-time')} />
        <OccasionLocationData
          occasion={occasion}
          className={cx('lia-occasion lia-occasion-location')}
        />
      </>
    );
  }

  /**
   * Renders the body part of the hover card
   */
  function renderBody(): React.ReactElement {
    return (
      <>
        {renderBoardIcon()}
        {renderSubject()}
        {renderAuthorLoginAndMessagePostTime()}
        {renderParentNode()}
        {showMessageStatus && renderMessageStatus()}
        {isOccasion && renderOccasionMetaData()}
        {showSolvedBadge && renderSolvedBadge()}
        {renderPreviewText()}
      </>
    );
  }

  /**
   * Renders the metaData part of the hover card.
   */
  function renderMetaData(): React.ReactElement {
    return (
      <div className={cx('lia-g-hovercard-data', { 'justify-content-between': hasAllMetaData })}>
        {showViewsCount && renderViewsCount()}
        {showAttendeeCount && renderAttendeeCount()}
        {showKudosCount && renderKudosCount()}
        {showRepliesCount && renderRepliesCount()}
      </div>
    );
  }

  /**
   * Renders the message subscription action.
   */
  function renderMessageSubscriptionAction(): React.ReactElement {
    return (
      <MessageSubscriptionAction
        message={message}
        variant={SubscriptionActionVariant.BUTTON}
        className={cx('lia-g-hovercard-btn')}
        useSuccessFeedback={false}
        useHoverEffect={true}
      />
    );
  }

  return (
    <div
      ref={ref}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cx('lia-g-hovercard')}
    >
      {renderBody()}
      {showMetadata && renderMetaData()}
      {renderMessageSubscriptionAction()}
    </div>
  );
});

export default MessageHoverCard;
